import { SiteFooter } from "@/components/SiteFooter";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useReservationModal } from "@/context/ReservationContext";
import { listPublishedMenu } from "@/lib/menu.functions";
import { mergeMenu, findMergedItem } from "@/lib/menu-merge";
import { useTrackEvent } from "@/lib/analytics";

export const Route = createFileRoute("/menu/$slug")({
  loader: async ({ params }) => {
    const dbRows = await listPublishedMenu();
    const item = findMergedItem(params.slug, dbRows);
    if (!item) throw notFound();
    const related = mergeMenu(dbRows)
      .filter((m) => m.category === item.category && m.slug !== item.slug)
      .slice(0, 3);
    return { item, related };
  },
  head: ({ loaderData, params }) => {
    const i = loaderData?.item;
    if (!i) return { meta: [{ title: "Menu — Plunkets Pub Dublin" }] };
    const url = `https://plunkets.ie/menu/${params.slug}`;
    const fallback = ["Signature Cocktails", "Classic Cocktails", "Beers on Tap"].includes(i.category)
      ? "https://plunkets.ie/og-cocktails.jpg?v=2"
      : "https://plunkets.ie/og-food.jpg";
    const image = i.image
      ? (i.image.startsWith("http") ? i.image : `https://plunkets.ie${i.image.startsWith("/") ? "" : "/"}${i.image}`)
      : fallback;
    const priceValue = i.price.replace(/[^0-9.]/g, "");
    return {
      meta: [
        { title: `${i.name} — ${i.category} | Plunkets Pub Dublin` },
        { name: "description", content: `${i.tagline} ${i.description.slice(0, 120)}` },
        { property: "og:title", content: `${i.name} — Plunkets Dublin` },
        { property: "og:description", content: i.tagline },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:alt", content: `${i.name} at Plunkets Pub Dublin` },
        ...(i.image ? [] : [
          { property: "og:image:width", content: "1200" },
          { property: "og:image:height", content: "630" },
        ]),
        { property: "og:site_name", content: "Plunkets Pub Dublin" },
        { property: "og:locale", content: "en_IE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@plunketspub" },
        { name: "twitter:title", content: `${i.name} — Plunkets Dublin` },
        { name: "twitter:description", content: i.tagline },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: `${i.name} at Plunkets Pub Dublin` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: i.name,
          description: i.description,
          image,
          category: i.category,
          brand: { "@type": "Brand", name: "Plunkets Pub Dublin" },
          offers: {
            "@type": "Offer",
            price: priceValue || undefined,
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url,
          },
        }),
      }],
    };
  },
  errorComponent: ({ error }) => (
    <main className="grid min-h-screen place-items-center bg-background p-6 text-center text-foreground">
      <p className="text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="grid min-h-screen place-items-center bg-background px-5 text-center text-foreground">
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-primary">404</p>
        <h1 className="mt-3 font-display text-3xl">Menu item not found</h1>
        <Link to="/cocktails-menu" className="mt-6 inline-block text-sm text-primary underline">
          Back to the menu
        </Link>
      </div>
    </main>
  ),
  component: MenuItemPage,
});

function MenuItemPage() {
  const { item, related } = Route.useLoaderData();
  const { open: openModal } = useReservationModal();
  useTrackEvent("menu_view", { menu_name: "item", menu_slug: item.slug, item_name: item.name, category: item.category });

  return (
    <main className="min-h-screen bg-background pb-24 text-foreground md:pb-0">
      <div className="mx-auto max-w-6xl px-5 py-8 md:px-8 md:py-14">
        <Link to="/cocktails-menu" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={16} /> Back to menu
        </Link>

        <div className="mt-6 grid gap-8 md:mt-10 lg:grid-cols-2 lg:gap-16">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-muted/40 md:rounded-3xl">
            {item.image && (
              <img
                src={item.image}
                alt={`${item.name} — ${item.tagline}`}
                width={1024}
                height={1024}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="aspect-square w-full object-cover"
              />
            )}
            {item.badge && (
              <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground sm:left-5 sm:top-5 sm:px-4 sm:py-1.5 sm:text-xs">
                {item.badge}
              </span>
            )}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-primary sm:text-xs sm:tracking-[0.4em]">{item.category}</p>
            <h1 className="mt-2 font-display text-3xl leading-[1] tracking-tight sm:text-4xl md:mt-3 md:text-5xl">{item.name}</h1>
            <p className="mt-3 text-base text-muted-foreground sm:mt-4 sm:text-lg">{item.tagline}</p>

            <div className="mt-5 flex items-baseline gap-3 sm:mt-6">
              <span className="font-display text-3xl text-gold-gradient sm:text-4xl">{item.price}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">incl. VAT</span>
            </div>

            <p className="mt-6 text-sm leading-relaxed text-foreground/90 sm:mt-8 sm:text-base">{item.description}</p>

            {item.ingredients.length > 0 && (
              <div className="mt-6 sm:mt-8">
                <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:text-xs">What's in it</p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {item.ingredients.map((ing: string) => (
                    <li
                      key={ing}
                      className="rounded-full border border-border bg-card/40 px-3 py-1.5 text-sm text-foreground/85"
                    >
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 hidden flex-wrap gap-3 md:mt-10 md:flex">
              <button
                onClick={() => openModal({ bookingType: "food_and_drinks" })}
                className="rounded-full bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition-all hover:opacity-90"
              >
                Book a Table
              </button>
              <Link
                to="/cocktails-menu"
                className="rounded-full border border-border px-6 py-3 text-sm font-semibold uppercase tracking-wider text-foreground transition-all hover:border-primary hover:text-primary"
              >
                Browse Menu
              </Link>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-16 sm:mt-20">
            <h2 className="font-display text-xl md:text-3xl">More from {item.category}</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:mt-6 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
              {related.map((r: typeof item) => (
                <Link
                  key={r.slug}
                  to="/menu/$slug"
                  params={{ slug: r.slug }}
                  className="group overflow-hidden rounded-2xl border border-border bg-card/30 transition-all hover:border-primary/50"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-muted/40">
                    {r.image && (
                      <img
                        src={r.image}
                        alt={r.name}
                        width={1024}
                        height={768}
                        loading="lazy"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-lg font-bold group-hover:text-primary">{r.name}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.tagline}</p>
                    <p className="mt-2 text-sm font-semibold text-primary">{r.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-semibold">{item.name}</p>
            <p className="text-xs text-primary">{item.price}</p>
          </div>
          <button
            onClick={() => openModal({ bookingType: "food_and_drinks" })}
            className="shrink-0 rounded-full bg-primary px-5 py-3 text-sm font-semibold uppercase tracking-wider text-primary-foreground"
          >
            Book a Table
          </button>
        </div>
      </div>
    <SiteFooter />
    </main>
  );
}
